import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, ViewChild, ElementRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription, forkJoin, firstValueFrom, throwError } from 'rxjs';
import { distinctUntilChanged, take, catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import {
  VisitasService,
  Visita,
  TipoVisita,
  Productor,
  AdjuntoItem,
  ObjetivoVisita,
  ModalidadContacto,
  obtenerDescripcionVisita,
  complementarDescripcionVisita,
  visitaTieneSolucion,
} from '../../services/visitas.service';
import { ResumenVisitaPdfService } from '../../services/resumen-visita-pdf.service';
import { NegocioService } from '../../services/negocio.service';
import { ProductorService } from '../../services/productor.service';
import { ToastService } from '../../services/toast.service';
import { NetworkService } from '../../services/network.service';
import { VisitaOfflinePanelService } from '../../services/visita-offline-panel.service';
import { AuthStore } from '../../../store/auth.store';
import { ValidationService } from '../../services/validation.service';
import { CrearVisitaRequestSchema, AgendarVisitaRequestSchema } from '../../schemas/visita.schema';
import { FILE_UPLOAD_ACCEPT, validateUploadFile } from '../../utils/file-upload-constraints';
import { aplicarEntradaFolio, folioRegistroValidators, normalizarFolioInput } from '../../utils/folio.util';
import {
  appendGenerarFolioAFormData,
  appendObjetivosVisitaAFormData,
  anexarObjetivosVisitaAPayloadJson,
  extraerGenerarFolioDesdeVisita,
  extraerIdModalidadDesdeVisita,
  extraerIdsObjetivoDesdeVisita,
  formatearModalidadContacto,
  formatearObjetivosVisita,
  ID_MODALIDAD_CONTACTO_PRESENCIAL,
  MAX_OBJETIVOS_VISITA_REGISTRO,
  normalizarIdsObjetivoVisita,
  normalizarTextosObjetivoDesdeApi,
  objetivosAgendarValidators,
  objetivosRegistroValidators,
} from '../../utils/visita-registro.util';
import { resolverAdjuntosVisitaDesdeModelo } from '../../utils/visita-adjuntos.util';
import {
  extraerAdjuntoResumenFirmadoDesdeVisita,
  type VisitaResumenFirmadoAdjunto,
} from '../../utils/visita-resumen-firmado-adjunto.util';
import { abrirODescargarAdjunto as abrirAdjuntoUtil } from '../../utils/abrir-adjunto.util';

/**
 * Tipo de acción que se está realizando con el formulario
 */
export type AccionVisita = 'nueva' | 'reagendar' | 'cancelar' | 'registrar' | 'ver-detalles' | 'subir-resumen-firmado';

/**
 * Componente de formulario lateral para gestionar visitas
 * Reutilizable para diferentes acciones: Nueva Visita, Reagendar, Cancelar, Registrar, Ver Detalles
 */
@Component({
  selector: 'app-formulario-visita',
  templateUrl: './formulario-visita.component.html',
  styleUrls: ['./formulario-visita.component.scss'],
  standalone: false
})
export class FormularioVisitaComponent implements OnInit, OnDestroy, OnChanges {
  @Input() accion: AccionVisita = 'nueva';
  @Input() visita?: Visita;
  @Input() visible: boolean = false;
  /** Cuando true, el contenido se muestra dentro de un ion-menu (sin overlay ni panel fijo). */
  @Input() dentroDeMenu: boolean = false;
  /** Indica si se permite la descarga de PDF en el detalle de la visita */
  @Input() permitirPdf: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() guardado = new EventEmitter<void>();

  cargando = false;
  guardando = false;

  /** Filtro de tipos en el selector de archivos (coincide con `validateUploadFile`). */
  readonly fileUploadAccept = FILE_UPLOAD_ACCEPT;

  // Listas de opciones
  productores: Productor[] = [];
  productoresFiltrados: Productor[] = [];
  tiposVisita: TipoVisita[] = [];
  tiposVisitaFiltrado: TipoVisita[] = [];
  /** Categorías de anexos cargadas desde GET /api/v1/visita/obtenerCategoriasAnexo (id = id_categoria_anexo) */
  categoriasAnexo: { id: number; descripcion: string }[] = [];
  /** Objetivos de visita para el selector al registrar (GET /visita/obtenerObjetivosVisita) */
  objetivosVisita: ObjetivoVisita[] = [];
  /** Modalidades de contacto (GET /visita/obtenerModalidadContacto) */
  modalidadesContacto: ModalidadContacto[] = [];
  readonly maxObjetivosVisitaRegistro = MAX_OBJETIVOS_VISITA_REGISTRO;

  // Búsqueda de productores
  terminoBusquedaProductor = '';
  cargandoProductores = false;

  // Fechas y horas
  fechaProgramadaISO: string | null = null;
  horaProgramada: string | null = null;

  // Campos para acción "Registrar" (API /registrar)
  fechaVisitaRegistroISO: string | null = null;
  horaInicioRegistro: string | null = null;
  horaTerminoRegistro: string | null = null;
  /**
   * Cada fila: archivo nuevo + categoría, o adjunto ya guardado en servidor (registro parcial).
   * Filas nuevas (`file`) o ya guardadas (`adjuntoGuardado`) se envían en POST /registrar (files + id_categorias).
   */
  itemsRegistroArchivos: {
    file: File | null;
    id_categoria: number | null;
    adjuntoGuardado?: AdjuntoItem | null;
  }[] = [];

  /** Detalle completo de la visita (ver-detalles y reagendar); incluye consultor, motivo, adjuntos. */
  visitaDetalle: Visita | null = null;

  visitaDiagnostico: Visita | null = null;

  /** Listado y acordeón en "Ver Detalles" (mismo patrón que historial-visitas en gestión). */
  visitasHistorialPanel: Visita[] = [];
  historialDetallesPorId: Record<string, Visita> = {};
  historialCargandoDetalleIds = new Set<string>();
  accordionHistorialAbierto: string | string[] | undefined;
  historialError: string | null = null;

  /** Id único para el trigger del popover de información "Es Emergencia". */
  readonly idEmergenciaInfo = 'formulario-visita-emergencia-info';

  /** Id único para el trigger del popover de información "Generar Folio". */
  readonly idGenerarFolioInfo = 'formulario-visita-generar-folio-info';

  /** Contenedor con scroll del panel (para llevar el scroll hasta el final al agregar archivo). */
  @ViewChild('formularioContent') formularioContentRef?: ElementRef<HTMLDivElement>;

  private subscriptions: Subscription = new Subscription();

  private readonly fb = inject(FormBuilder);
  private readonly visitasService = inject(VisitasService);
  private readonly resumenVisitaPdfService = inject(ResumenVisitaPdfService);
  private readonly negocioService = inject(NegocioService);
  private readonly productorService = inject(ProductorService);
  private readonly authStore = inject(AuthStore);
  private readonly toastService = inject(ToastService);
  private readonly validationService = inject(ValidationService);
  private readonly networkService = inject(NetworkService);
  private readonly visitaOfflinePanel = inject(VisitaOfflinePanelService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  formulario: FormGroup = this.fb.group({
    id_productor: ['', Validators.required],
    lugar: ['', Validators.required],
    fecha_programada: [''], // No requerido en FormGroup, se valida manualmente
    hora_programada: [''], // No requerido en FormGroup, se valida manualmente
    id_tipo_visita: ['', Validators.required],
    id_objetivo_visita: [[] as number[], objetivosAgendarValidators()],
    objetivo_otro: [''],
    comentarios: [''],
    descripcion: [''],
    descripcion_emergencia: [''],
    archivo_anexo: [null],
    categoria_anexo: [''],
    es_contingencia: [null as boolean | null, Validators.required],
    bolsa_visita: [false]
  });

  /** Formulario para registrar visita (POST /visita/{id}/registrar) */
  formularioRegistro: FormGroup = this.fb.group({
    tipo_reporte: [null as 'digital' | 'papel' | null, Validators.required],
    id_modalidad_contacto: [null as number | null],
    id_objetivo_visita: [[] as number[]],
    objetivo_otro: [''],
    generar_folio: [false],
    folio: [''],
    recomendaciones: [''],
    compromisos: ['']
  });

  ngOnInit() {
    this.cargarTiposVisita();
    this.cargarCategoriasAnexo();
    this.cargarObjetivosVisita();
    this.cargarModalidadesContacto();
    this.cargarProductores();
    this.formulario.get('categoria_anexo')?.disable({ emitEvent: false });
    this.aplicarValidadoresRegistroPorTipo(null);
    // Forzar actualización de la vista cuando cambia cualquier valor del formulario (para que el botón se habilite)
    const valueSub = this.formulario.valueChanges?.subscribe(() => this.cdr.markForCheck());
    if (valueSub) this.subscriptions.add(valueSub);
    const statusSub = this.formulario.statusChanges?.subscribe(() => this.cdr.markForCheck());
    if (statusSub) this.subscriptions.add(statusSub);
    const regValueSub = this.formularioRegistro.valueChanges?.subscribe(() => this.cdr.markForCheck());
    if (regValueSub) this.subscriptions.add(regValueSub);
    const regStatusSub = this.formularioRegistro.statusChanges?.subscribe(() => this.cdr.markForCheck());
    if (regStatusSub) this.subscriptions.add(regStatusSub);
    const tipoReporteSub = this.formularioRegistro
      .get('tipo_reporte')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((tipo) => this.aplicarValidadoresRegistroPorTipo(tipo as 'digital' | 'papel' | null | undefined));
    if (tipoReporteSub) this.subscriptions.add(tipoReporteSub);
    const generarFolioSub = this.formularioRegistro
      .get('generar_folio')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe(() => this.aplicarValidadoresFolioRegistro());
    if (generarFolioSub) this.subscriptions.add(generarFolioSub);

    const tipoVisitaSub = this.formulario
      .get('id_tipo_visita')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe(() => {
        this.aplicarRestriccionBolsaVisita();
        this.cdr.markForCheck();
      });
    if (tipoVisitaSub) this.subscriptions.add(tipoVisitaSub);

    const objetivoSub = this.formulario
      .get('id_objetivo_visita')
      ?.valueChanges.subscribe((ids) => this.aplicarValidadoresObjetivoOtro(this.formulario, ids));
    if (objetivoSub) this.subscriptions.add(objetivoSub);

    const objetivoRegSub = this.formularioRegistro
      .get('id_objetivo_visita')
      ?.valueChanges.subscribe((ids) => this.aplicarValidadoresObjetivoOtro(this.formularioRegistro, ids));
    if (objetivoRegSub) this.subscriptions.add(objetivoRegSub);

    // Aplicar validadores iniciales para detectar si ya hay objetivo 13 seleccionado al cargar
    this.aplicarValidadoresObjetivoOtro(this.formulario, this.formulario.get('id_objetivo_visita')?.value);
    this.aplicarValidadoresObjetivoOtro(this.formularioRegistro, this.formularioRegistro.get('id_objetivo_visita')?.value);
  }

  private aplicarValidadoresObjetivoOtro(form: FormGroup, ids: number[] | null | undefined): void {
    const ctrl = form.get('objetivo_otro');
    if (!ctrl) return;
    if (Array.isArray(ids) && ids.includes(13)) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  /**
   * Ajusta validadores según tipo de reporte (digital vs papel).
   * Objetivo y folio obligatorios cuando ya hay tipo seleccionado (salvo generar_folio).
   */
  private aplicarValidadoresRegistroPorTipo(tipo: 'digital' | 'papel' | null | undefined): void {
    const modalidadCtrl = this.formularioRegistro.get('id_modalidad_contacto');
    const idCtrl = this.formularioRegistro.get('id_objetivo_visita');
    const recCtrl = this.formularioRegistro.get('recomendaciones');
    const compCtrl = this.formularioRegistro.get('compromisos');
    if (tipo === 'digital' || tipo === 'papel') {
      modalidadCtrl?.setValidators([Validators.required]);
      idCtrl?.setValidators(objetivosRegistroValidators());
    } else {
      modalidadCtrl?.clearValidators();
      idCtrl?.clearValidators();
    }
    if (tipo === 'digital') {
      recCtrl?.setValidators([Validators.required, Validators.maxLength(500)]);
      compCtrl?.setValidators([Validators.required, Validators.maxLength(500)]);
    } else {
      recCtrl?.setValidators([Validators.maxLength(500)]);
      compCtrl?.setValidators([Validators.maxLength(500)]);
    }
    modalidadCtrl?.updateValueAndValidity({ emitEvent: false });
    idCtrl?.updateValueAndValidity({ emitEvent: false });
    recCtrl?.updateValueAndValidity({ emitEvent: false });
    compCtrl?.updateValueAndValidity({ emitEvent: false });
    this.aplicarValidadoresFolioRegistro();
    this.cdr.markForCheck();
  }

  /** Folio manual obligatorio solo con tipo de reporte y sin «Generar Folio». */
  esFolioRegistroManualRequerido(): boolean {
    const tipo = this.formularioRegistro.get('tipo_reporte')?.value;
    return (tipo === 'digital' || tipo === 'papel') && !this.esGenerarFolioRegistroActivo();
  }

  /** false cuando «Generar Folio» está marcado (folio lo asigna el backend). */
  esFolioRegistroEditable(): boolean {
    return !this.esGenerarFolioRegistroActivo();
  }

  private esGenerarFolioRegistroActivo(): boolean {
    return this.formularioRegistro.get('generar_folio')?.value === true;
  }

  private aplicarValidadoresFolioRegistro(): void {
    const folioCtrl = this.formularioRegistro.get('folio');
    const generar = this.esGenerarFolioRegistroActivo();
    const tipo = this.formularioRegistro.get('tipo_reporte')?.value as 'digital' | 'papel' | null;
    if (generar) {
      folioCtrl?.clearValidators();
      folioCtrl?.setValue('', { emitEvent: false });
      folioCtrl?.disable({ emitEvent: false });
    } else {
      folioCtrl?.enable({ emitEvent: false });
      if (tipo === 'digital' || tipo === 'papel') {
        folioCtrl?.setValidators(folioRegistroValidators());
      } else {
        folioCtrl?.clearValidators();
      }
    }
    folioCtrl?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  /** Catálogo de objetivos (GET /visita/obtenerObjetivosVisita) */
  private cargarObjetivosVisita(): void {
    this.visitasService.obtenerObjetivosVisita().subscribe({
      next: (list) => {
        this.objetivosVisita = Array.isArray(list) ? list : [];
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error al cargar objetivos de visita:', error);
        this.objetivosVisita = [];
        this.cdr.markForCheck();
      }
    });
  }

  /** Catálogo de modalidades (GET /visita/obtenerModalidadContacto) */
  private cargarModalidadesContacto(): void {
    this.visitasService.obtenerModalidadContacto().subscribe({
      next: (list) => {
        this.modalidadesContacto = Array.isArray(list) ? list : [];
        this.aplicarModalidadContactoSegunBolsa();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error al cargar modalidades de contacto:', error);
        this.modalidadesContacto = [];
        this.cdr.markForCheck();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Cuando el formulario se hace visible
    if (changes['visible'] && changes['visible'].currentValue === true) {
      // Asegurar que el formulario esté habilitado antes de cargar datos
      this.formulario.enable();
      // Recargar productores con el perfil/jerarquía actual (por si cambió desde la última apertura)
      this.cargarProductores();
      
      // Si hay una visita, cargar sus datos (especialmente importante para reagendar)
      if (this.visita) {
        this.cargarDatosVisita();
      } else if (this.accion === 'nueva') {
        // Establecer valores por defecto para nueva visita
        this.establecerValoresPorDefecto();
      }
      
      // Después de cargar datos, aplicar restricciones según la acción
      this.aplicarRestriccionesSegunAccion();
    }
    
    // Cuando se cambia la acción o la visita
    if (changes['accion'] || changes['visita']) {
      if (this.visible) {
        // Asegurar que el formulario esté habilitado antes de cargar datos
        this.formulario.enable();
        
        // Si hay una visita, siempre cargar sus datos (especialmente para reagendar)
        if (this.visita) {
          this.cargarDatosVisita();
        } else if (this.accion === 'nueva') {
          this.establecerValoresPorDefecto();
        }
        
        // Después de cargar datos, aplicar restricciones según la acción
        this.aplicarRestriccionesSegunAccion();
      }
    }
  }

  /**
   * Establece valores por defecto para fecha y hora.
   * La hora programada por defecto es 00:00.
   * Para "Agendar Nueva Visita" también deja el campo Usuario INDAP vacío (búsqueda y selector).
   */
  private establecerValoresPorDefecto(): void {
    // Usuario INDAP: siempre vacío al abrir; el selector debe mostrar "Seleccione un usuario"
    this.terminoBusquedaProductor = '';
    this.productoresFiltrados = Array.isArray(this.productores) ? [...this.productores] : [];
    this.formulario.patchValue({ id_productor: '' });
    this.formulario.get('id_productor')?.updateValueAndValidity({ emitEvent: true });

    const ahora = new Date();
    const fechaHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
    this.fechaProgramadaISO = fechaHoy.toISOString();

    // Hora por defecto no obligatoria para agendar/reagendar
    const horaPorDefecto = '00:00';
    this.horaProgramada = horaPorDefecto;

    this.formulario.patchValue({
      fecha_programada: this.fechaProgramadaISO,
      hora_programada: horaPorDefecto,
      es_contingencia: null,
      bolsa_visita: false,
      descripcion: '',
      descripcion_emergencia: '',
      id_objetivo_visita: [],
      objetivo_otro: ''
    });

    this.cdr.markForCheck();
    setTimeout(() => {
      if (!this.fechaProgramadaISO) {
        this.fechaProgramadaISO = fechaHoy.toISOString();
      }
      if (!this.horaProgramada) {
        this.horaProgramada = horaPorDefecto;
      }
      this.cdr.markForCheck();
    }, 100);
  }

  /**
   * Asegura que el valor del select de productor se aplique y la vista se actualice (estado del botón)
   */
  onProductorChange(event: any): void {
    const value = event.detail?.value;
    this.formulario.patchValue({ id_productor: value ?? '' });
    this.formulario.get('id_productor')?.updateValueAndValidity({ emitEvent: true });
    this.cdr.detectChanges();
    this.validaDiagnostico(this.formulario.get('id_productor')?.value)
    const hoy = new Date();
    const inicioHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    
    this.fechaProgramadaISO = inicioHoy.toISOString()
  }

  validaDiagnostico(id_productor: string){
    this.visitasService.obtenerVisitaDiagnosticoProd(id_productor).subscribe({
      next: (visita) => {
        if(visita){
          this.visitaDiagnostico = visita
          this.tiposVisitaFiltrado = this.tiposVisita
        }
        else{
          this.visitaDiagnostico = null
          this.tiposVisitaFiltrado = this.tiposVisita.filter(s => s.id_tipo_visita == 1)
        }

      },
      error: (error) => {
        this.visitaDiagnostico = null
        this.tiposVisitaFiltrado = this.tiposVisita.filter(s => s.id_tipo_visita == 1)
      }
    });
   
  }

  obtenerTiposVisita(){
    if(this.formulario.get('id_productor')?.value)
      return this.tiposVisitaFiltrado
    else
      return this.tiposVisita
  }

  /**
   * Asegura que el valor del select de tipo de visita se aplique y la vista se actualice (estado del botón)
   */
  onTipoVisitaChange(event: any): void {
    const value = event.detail?.value;
    this.formulario.patchValue({ id_tipo_visita: value ?? '' });
    this.formulario.get('id_tipo_visita')?.updateValueAndValidity({ emitEvent: true });
    this.cdr.detectChanges(); 
  }

  /**
   * Fuerza actualización de la vista al cambiar un campo de texto (para que se revalide el botón)
   */
  onCampoTextoChange(): void {
    this.cdr.detectChanges();
  }

  /** Placeholder del select de usuario: indica carga del listado solo durante búsqueda con término (no en carga inicial). */
  get placeholderSelectUsuarioIndap(): string {
    if (this.cargandoProductores && this.terminoBusquedaProductor.trim().length > 0) {
      return 'Buscando...';
    }
    return 'Seleccione un usuario';
  }

  /** Búsqueda con término terminó sin resultados (no muestra mientras sigue cargando). */
  get mostrarAvisoBusquedaProductorSinResultados(): boolean {
    return (
      this.terminoBusquedaProductor.trim().length > 0 &&
      !this.cargandoProductores &&
      this.productoresFiltrados.length === 0
    );
  }

  /**
   * Maneja la búsqueda de productores
   */
  onBuscarProductor(event: any): void {
    const termino = event.detail.value || '';
    this.terminoBusquedaProductor = termino;
    
    if (termino.trim().length > 0) {
      this.buscarProductores(termino);
    } else {
      // Asegurar que siempre sea un array
      this.productoresFiltrados = Array.isArray(this.productores) ? this.productores : [];
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Carga los tipos de visita desde la API
   */
  private cargarTiposVisita(): void {
    this.visitasService.obtenerTiposVisita().subscribe({
      next: (tipos) => {
        this.tiposVisita = Array.isArray(tipos) ? tipos : [];
        // Si estamos en reagendar con visita, resolver id_tipo_visita ahora (tipos ya cargados)
        if (this.visible && this.accion === 'reagendar' && this.visita?.tipo) {
          const idTipo = this.obtenerIdTipoVisita(this.visita.tipo);
          if (idTipo != null) {
            this.formulario.patchValue({ id_tipo_visita: idTipo });
            this.cdr.markForCheck();
          }
        }
      },
      error: (error) => {
        console.error('Error al cargar tipos de visita:', error);
        this.tiposVisita = [];
      }
    });
  }

  /**
   * Carga las categorías de anexos desde GET /api/v1/visita/obtenerCategoriasAnexo.
   * Se usan en "Registrar Visita" (selector por archivo) y en el formulario estándar (categoría del anexo).
   */
  private cargarCategoriasAnexo(): void {
    this.visitasService.obtenerCategoriasAnexo().subscribe({
      next: (categorias) => {
        this.categoriasAnexo = (Array.isArray(categorias) ? categorias : []).map(c => ({
          id: c.id_categoria_anexo,
          descripcion: c.descripcion
        }));
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error al cargar categorías de anexo:', error);
        this.categoriasAnexo = [];
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Carga los productores desde la API.
   * Usa GET /api/v1/productor/perfil/{id_perfil}/jerarquia/{id_jerarquia}
   */
  private cargarProductores(): void {
    const perfil = this.authStore.obtenerPerfilActivo();
    const idJerarquia = this.authStore.obtenerIdJerarquiaParaEndpoint();

    if (perfil?.id_perfil == null) {
      this.productores = [];
      this.productoresFiltrados = [];
      this.cargandoProductores = false;
      return;
    }

    this.cargandoProductores = true;
    this.productorService.obtenerProductores(perfil.id_perfil, idJerarquia).subscribe({
      next: (productores) => {
        this.productores = Array.isArray(productores) ? productores : [];
        this.productoresFiltrados = Array.isArray(productores) ? productores : [];
        this.cargandoProductores = false;
      },
      error: (error) => {
        console.error('Error al cargar productores:', error);
        this.productores = [];
        this.productoresFiltrados = [];
        this.cargandoProductores = false;
      }
    });
  }

  /**
   * Busca productores por término de búsqueda.
   * Usa GET /api/v1/productor/perfil/{id_perfil}/jerarquia/{id_jerarquia} con param search
   */
  private buscarProductores(termino: string): void {
    const perfil = this.authStore.obtenerPerfilActivo();
    const idJerarquia = this.authStore.obtenerIdJerarquiaParaEndpoint();

    if (perfil?.id_perfil == null) {
      this.productoresFiltrados = [];
      this.cargandoProductores = false;
      return;
    }

    this.cargandoProductores = true;
    this.productorService.obtenerProductores(perfil.id_perfil, idJerarquia, termino).subscribe({
      next: (productores) => {
        this.productoresFiltrados = Array.isArray(productores) ? productores : [];
        this.cargandoProductores = false;
      },
      error: (error) => {
        console.error('Error al buscar productores:', error);
        this.productoresFiltrados = [];
        this.cargandoProductores = false;
      }
    });
  }

  /**
   * Carga los datos de la visita en el formulario
   */
  private cargarDatosVisita(): void {
    if (!this.visita) return;

    // Asegurar que el formulario esté habilitado antes de cargar datos
    this.formulario.enable();

    // Limpiar valores previos antes de cargar nuevos datos (especialmente importante para reagendar)
    // Esto asegura que no queden valores por defecto de una acción anterior
    this.fechaProgramadaISO = null;
    this.horaProgramada = null;

    if (this.accion === 'ver-detalles' && this.visita.id_visita) {
      this.cargarVistaDetallesConHistorial();
      return;
    }

    if (this.accion === 'reagendar' && this.visita.id_visita) {
      this.cargando = true;
      this.visitasService.obtenerDetallesVisita(this.visita.id_visita).subscribe({
        next: (visitaCompleta) => {
          const detalle = complementarDescripcionVisita(visitaCompleta, this.visita);
          this.visitaDetalle = detalle;
          this.llenarFormulario(detalle);
          this.aplicarRestriccionesSegunAccion();
          this.cargando = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error al cargar detalles de visita:', error);
          this.toastService.mostrar('Error al cargar los detalles de la visita', 'error');
          this.visitaDetalle = null;
          if (this.visita) this.llenarFormulario(this.visita);
          this.aplicarRestriccionesSegunAccion();
          this.cargando = false;
          this.cdr.markForCheck();
        }
      });
    } else if (this.accion === 'registrar' && this.visita.id_visita) {
      this.cargando = true;
      this.visitasService.obtenerDetallesVisita(this.visita.id_visita).subscribe({
        next: (visitaCompleta) => {
          const detalle = complementarDescripcionVisita(visitaCompleta, this.visita);
          this.visitaDetalle = detalle;
          this.llenarFormulario(detalle);
          this.llenarDatosRegistroDesdeVisita(detalle);
          this.aplicarRestriccionesSegunAccion();
          this.cargando = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error al cargar detalles de visita:', error);
          this.toastService.mostrar('Error al cargar los detalles de la visita', 'error');
          this.visitaDetalle = null;
          if (this.visita) {
            this.llenarFormulario(this.visita);
            this.llenarDatosRegistroDesdeVisita(this.visita);
          }
          this.aplicarRestriccionesSegunAccion();
          this.cargando = false;
          this.cdr.markForCheck();
        }
      });
    } else {
      this.visitaDetalle = null;
      this.llenarFormulario(this.visita);
      this.aplicarRestriccionesSegunAccion();
      this.cdr.markForCheck();
    }
  }

  private limpiarEstadoHistorialPanel(): void {
    this.visitasHistorialPanel = [];
    this.historialDetallesPorId = {};
    this.historialCargandoDetalleIds.clear();
    this.accordionHistorialAbierto = undefined;
    this.historialError = null;
  }

  /**
   * Ver Detalles: historial del productor en acordeón (como /gestion/.../historial-visitas);
   * la visita consultada queda expandida por defecto.
   */
  private cargarVistaDetallesConHistorial(): void {
    if (!this.visita?.id_visita) return;
    this.cargando = true;
    this.historialError = null;
    this.limpiarEstadoHistorialPanel();
    const idVisita = this.visita.id_visita;
    const idProd = this.visita.productor?.id_productor;

    const aplicarDetalleYFormulario = (detalle: Visita) => {
      const detalleCompleto = complementarDescripcionVisita(detalle, this.visita);
      this.visitaDetalle = detalleCompleto;
      this.historialDetallesPorId[idVisita] = detalleCompleto;
      this.llenarFormulario(detalleCompleto);
      this.aplicarRestriccionesSegunAccion();
    };

    const terminarCarga = () => {
      this.cargando = false;
      this.cdr.markForCheck();
      this.programarScrollHistorialAccordion(idVisita);
    };

    const cargarSoloDetalle = () => {
      this.visitasHistorialPanel = [this.visita!];
      this.accordionHistorialAbierto = idVisita;
      this.visitasService.obtenerDetallesVisita(idVisita).subscribe({
        next: (detalle) => {
          this.historialError = null;
          aplicarDetalleYFormulario(detalle);
          terminarCarga();
        },
        error: (error) => {
          console.error('Error al cargar detalles de visita:', error);
          this.toastService.mostrar('Error al cargar los detalles de la visita', 'error');
          this.visitaDetalle = null;
          this.llenarFormulario(this.visita!);
          this.aplicarRestriccionesSegunAccion();
          terminarCarga();
        }
      });
    };

    if (!idProd) {
      cargarSoloDetalle();
      return;
    }

    forkJoin({
      lista: this.visitasService.obtenerVisitasPorProductor(idProd, { order: 'desc', sort: 'fecha', size: 100 }),
      detalle: this.visitasService.obtenerDetallesVisita(idVisita)
    }).subscribe({
      next: ({ lista, detalle }) => {
        this.historialError = null;
        let items = lista.items ?? [];
        if (!items.some((v) => v.id_visita === idVisita)) {
          items = [this.visita!, ...items];
        }
        this.visitasHistorialPanel = items;
        this.accordionHistorialAbierto = idVisita;
        aplicarDetalleYFormulario(detalle);
        terminarCarga();
      },
      error: (error) => {
        console.error('Error al cargar historial de visitas:', error);
        this.historialError = 'No se pudo cargar el historial completo; mostrando solo la visita consultada.';
        cargarSoloDetalle();
      }
    });
  }

  onAccordionHistorialChange(ev: Event): void {
    const ionEv = ev as CustomEvent<{ value: string | string[] | undefined }>;
    this.accordionHistorialAbierto = ionEv.detail?.value;
    const id = Array.isArray(this.accordionHistorialAbierto)
      ? this.accordionHistorialAbierto[0]
      : this.accordionHistorialAbierto;
    if (id && !this.historialDetallesPorId[id]) {
      this.cargarDetalleHistorialVisita(id);
    }
    if (id) {
      this.programarScrollHistorialAccordion(id);
    }
  }

  /**
   * Desplaza el scroll del panel hasta el acordeón abierto (apertura manual o carga inicial).
   */
  private programarScrollHistorialAccordion(idVisita: string): void {
    if (this.accion !== 'ver-detalles' || !idVisita) return;
    const ejecutar = () => this.scrollHistorialAccordionIntoView(idVisita);
    requestAnimationFrame(() => {
      ejecutar();
      setTimeout(ejecutar, 120);
      setTimeout(ejecutar, 360);
    });
  }

  private scrollHistorialAccordionIntoView(idVisita: string): void {
    const root = this.formularioContentRef?.nativeElement;
    if (!root || !idVisita) return;
    const acc = root.querySelector(`ion-accordion[data-historial-id="${idVisita}"]`) as HTMLElement | null;
    acc?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private cargarDetalleHistorialVisita(idVisita: string): void {
    if (this.historialCargandoDetalleIds.has(idVisita)) return;
    this.historialCargandoDetalleIds.add(idVisita);
    this.visitasService.obtenerDetallesVisita(idVisita).subscribe({
      next: (detalle) => {
        const itemLista = this.visitasHistorialPanel.find((v) => v.id_visita === idVisita);
        this.historialDetallesPorId[idVisita] = complementarDescripcionVisita(detalle, itemLista);
        this.historialCargandoDetalleIds.delete(idVisita);
        this.cdr.markForCheck();
      },
      error: () => {
        this.historialCargandoDetalleIds.delete(idVisita);
        this.cdr.markForCheck();
      }
    });
  }

  /** Misma lógica que contenido-resumen-visitas (badges de estado). */
  getClaseEstadoHistorialVisita(estado: string): string {
    const e = estado?.toLowerCase() || '';
    switch (e) {
      case 'agendada':
        return 'estado-agendada';
      case 'planificada':
        return 'estado-planificada';
      case 'finalizada':
      case 'completada':
        return 'estado-finalizada';
      case 'cancelada':
        return 'estado-cancelada';
      case 'reagendada':
        return 'estado-reagendada';
      default:
        return 'estado-default';
    }
  }

  formatearFechaHistorialVisita(fechaISO: string | null | undefined): string {
    if (!fechaISO) return 'S/I';
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  formatearHoraHistorialVisita(fechaISO: string | null | undefined): string {
    if (!fechaISO) return 'S/I';
    const d = new Date(fechaISO);
    return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  }

  lineasCompromisosHistorial(compromisos: string | null | undefined): string[] {
    if (!compromisos?.trim()) return [];
    return compromisos
      .split(/\n|•|[-–—]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  /**
   * Genera y descarga el PDF del resumen de la visita.
   * Asegura que el detalle completo esté cargado antes de generar.
   */
  async descargarPDF(event: Event, item: Visita): Promise<void> {
    event.stopPropagation();
    
    if (!item.id_visita) return;

    try {
      this.toastService.mostrar('Generando reporte PDF...', 'success');
      
      let visitaCompleta = this.historialDetallesPorId[item.id_visita];
      
      if (!visitaCompleta) {
        const detalleApi =
          (await this.visitasService.obtenerDetallesVisita(item.id_visita).toPromise()) || item;
        visitaCompleta = complementarDescripcionVisita(detalleApi, item);
        this.historialDetallesPorId[item.id_visita] = visitaCompleta;
      }

      await this.resumenVisitaPdfService.descargarResumenVisita({ visita: visitaCompleta });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      this.toastService.mostrar('No se pudo generar el reporte PDF', 'error');
    }
  }

  private sincronizarGenerarFolioRegistro(valor: boolean): void {
    this.formularioRegistro.get('generar_folio')?.setValue(valor, { emitEvent: false });
    this.aplicarValidadoresFolioRegistro();
    this.cdr.markForCheck();
  }

  /**
   * Rellena el formulario de registro. Tras GET /visita/{id}, si es_parcial es true restaura el borrador;
   * si no, predefine fecha/hora desde la visita programada.
   */
  private llenarDatosRegistroDesdeVisita(visita: Visita): void {
    this.fechaVisitaRegistroISO = null;
    this.horaInicioRegistro = null;
    this.horaTerminoRegistro = null;
    this.itemsRegistroArchivos = [];
    this.formularioRegistro.reset({
      tipo_reporte: null,
      id_modalidad_contacto: null,
      id_objetivo_visita: [],
      objetivo_otro: '',
      generar_folio: false,
      folio: '',
      recomendaciones: '',
      compromisos: ''
    });
    this.aplicarValidadoresRegistroPorTipo(null);
    this.aplicarModalidadContactoSegunBolsa();

    if (visita.es_parcial === true) {
      this.aplicarDatosRegistroParcial(visita);
      return;
    }

    this.sincronizarGenerarFolioRegistro(extraerGenerarFolioDesdeVisita(visita));

    if (visita.fecha) {
      const fecha = new Date(visita.fecha);
      if (!isNaN(fecha.getTime())) {
        const fechaSolo = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
        this.fechaVisitaRegistroISO = fechaSolo.toISOString();
        this.horaInicioRegistro = `${String(fecha.getHours()).padStart(2, '0')}:${String(fecha.getMinutes()).padStart(2, '0')}`;
        this.horaTerminoRegistro = this.obtenerHoraInicioMasUnaHora(this.horaInicioRegistro);
      }
    }
  }

  /** Restaura campos del formulario desde GET /visita/{id} cuando es_parcial es true. */
  private aplicarDatosRegistroParcial(visita: Visita): void {
    const fechaVisitaStr = visita.fecha_visita?.trim() || '';
    if (fechaVisitaStr) {
      const partes = fechaVisitaStr.split('-').map((s) => parseInt(s, 10));
      if (partes.length >= 3 && partes[0] > 0 && partes[1] >= 1 && partes[1] <= 12 && partes[2] >= 1 && partes[2] <= 31) {
        const inicio = new Date(partes[0], partes[1] - 1, partes[2]);
        this.fechaVisitaRegistroISO = inicio.toISOString();
      } else {
        const parsed = this.parseFechaHoraApi(fechaVisitaStr);
        if (parsed) {
          const inicio = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
          this.fechaVisitaRegistroISO = inicio.toISOString();
        }
      }
    }

    const horaInicioRaw = visita.hora_inicio?.trim() || '';
    if (horaInicioRaw) {
      const partesHora = horaInicioRaw.split(':');
      if (partesHora.length >= 2) {
        this.horaInicioRegistro = `${partesHora[0].padStart(2, '0')}:${partesHora[1].padStart(2, '0')}`;
      }
    }

    const horaTerminoRaw = visita.hora_termino?.trim() || '';
    if (horaTerminoRaw) {
      const partesHora = horaTerminoRaw.split(':');
      if (partesHora.length >= 2) {
        this.horaTerminoRegistro = `${partesHora[0].padStart(2, '0')}:${partesHora[1].padStart(2, '0')}`;
      }
    } else if (this.horaInicioRegistro) {
      this.horaTerminoRegistro = this.obtenerHoraInicioMasUnaHora(this.horaInicioRegistro);
    }

    const tipoRaw = visita.tipo_reporte?.trim() || '';
    const tipoReporte =
      tipoRaw === 'digital' || tipoRaw === 'papel' ? (tipoRaw as 'digital' | 'papel') : null;
    const idsObjetivo = this.resolverIdsObjetivoVisita(visita);
    const idModalidad = extraerIdModalidadDesdeVisita(visita);
    const generarFolio = extraerGenerarFolioDesdeVisita(visita);
    const folio = generarFolio ? '' : normalizarFolioInput(visita.folio ?? '');
    const recomendaciones = visita.recomendaciones ?? '';
    const compromisos = visita.compromisos ?? '';

    this.formularioRegistro.patchValue(
      {
        tipo_reporte: tipoReporte,
        id_objetivo_visita: idsObjetivo,
        objetivo_otro: visita.objetivo_otro || '',
        id_modalidad_contacto: idModalidad,
        folio,
        recomendaciones,
        compromisos,
      },
      { emitEvent: false }
    );
    this.sincronizarGenerarFolioRegistro(generarFolio);
    this.aplicarValidadoresRegistroPorTipo(tipoReporte);
    this.aplicarModalidadContactoSegunBolsa();

    const adjuntos = this.obtenerAdjuntosVisita(visita);
    if (adjuntos.length > 0) {
      this.itemsRegistroArchivos = adjuntos.map((adj) => ({
        file: null,
        id_categoria: adj.id_categoria ?? null,
        adjuntoGuardado: adj,
      }));
    }
  }

  /** Adjuntos del GET detalle o del listado (registro_visita) para restaurar borrador parcial. */
  private obtenerAdjuntosVisita(visita: Visita): AdjuntoItem[] {
    return resolverAdjuntosVisitaDesdeModelo(visita) ?? [];
  }

  /** true si la fila corresponde a un adjunto ya persistido (no requiere selector de archivo). */
  esAdjuntoRegistroGuardado(item: { adjuntoGuardado?: AdjuntoItem | null }): boolean {
    return !!item?.adjuntoGuardado;
  }

  /**
   * OpenAPI GET detalle expone objetivo (texto); id_objetivo_visita viene del listado o del catálogo.
   */
  private resolverIdsObjetivoVisita(visita: Visita): number[] {
    const desdeIds = extraerIdsObjetivoDesdeVisita(visita);
    if (desdeIds.length) return desdeIds;
    const textos = [
      ...normalizarTextosObjetivoDesdeApi(visita.objetivos ?? null),
      ...normalizarTextosObjetivoDesdeApi(visita.objetivo ?? null),
    ];
    if (!textos.length) return [];
    const ids = textos
      .map((texto) =>
        this.objetivosVisita.find(
          (o) => o.descripcion.trim().toLowerCase() === texto.toLowerCase()
        )?.id_objetivo_visita
      )
      .filter((id): id is number => id != null);
    return normalizarIdsObjetivoVisita(ids);
  }

  /** Si bolsa_visita es false, modalidad fija en Presencial (id 1) y deshabilitada. */
  private aplicarModalidadContactoSegunBolsa(): void {
    const ctrl = this.formularioRegistro.get('id_modalidad_contacto');
    if (!ctrl) return;
    const visitaActiva = this.getVisitaActiva();
    if (visitaActiva?.bolsa_visita === true) {
      ctrl.enable({ emitEvent: false });
      return;
    }
    ctrl.setValue(ID_MODALIDAD_CONTACTO_PRESENCIAL, { emitEvent: false });
    ctrl.disable({ emitEvent: false });
  }

  /**
   * Llena el formulario con los datos de la visita
   */
  /** true cuando se agenda una visita en estado Planificada (misma acción reagendar, distinto UI). */
  esAgendarDesdePlanificada(): boolean {
    if (this.accion !== 'reagendar' || !this.visita?.estado) return false;
    return this.visita.estado.trim().toLowerCase() === 'planificada';
  }

  esTipoVisitaDeshabilitado(): boolean {
    return this.esAgendarDesdePlanificada() || this.esCampoDeshabilitado();
  }

  /** Parsea fechas API (`ISO` o `yyyy-MM-dd HH:mm:ss`). */
  private parseFechaHoraApi(valor: string | null | undefined): Date | null {
    if (!valor?.trim()) return null;
    const normalized = valor.trim().replace(' ', 'T');
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }

  private getVisitaActiva(): Visita | null {
    return this.visitaDetalle ?? this.visita ?? null;
  }

  private debeAplicarRangoSolucion(): boolean {
    return this.esAgendarDesdePlanificada() && this.visitaTieneRangoPlanificado(this.getVisitaActiva());
  }

  private obtenerFechaPlanificadaDesdeVisita(
    visita: Visita | null | undefined,
    campo: 'fecha_inicio_planificada' | 'fecha_termino_planificada'
  ): string | null {
    if (!visita) return null;
    const raiz = visita[campo];
    if (raiz?.trim()) return raiz.trim();
    const reg = visita.registro_visita?.[campo];
    return reg?.trim() ?? null;
  }

  private visitaTieneRangoPlanificado(visita?: Visita | null): boolean {
    return !!this.obtenerFechaPlanificadaDesdeVisita(visita, 'fecha_inicio_planificada');
  }

  private getInicioDiaISO(d: Date): string {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
  }

  private getFechasPlanificacionSolucion(visitaOverride?: Visita | null): { inicio: Date; termino: Date } | null {
    const visita = visitaOverride ?? this.getVisitaActiva();
    if (!this.esAgendarDesdePlanificada() || !this.visitaTieneRangoPlanificado(visita)) return null;

    const inicioStr = this.obtenerFechaPlanificadaDesdeVisita(visita, 'fecha_inicio_planificada');
    const terminoStr = this.obtenerFechaPlanificadaDesdeVisita(visita, 'fecha_termino_planificada');
    if (!inicioStr || !terminoStr) return null;

    const inicioRaw = this.parseFechaHoraApi(inicioStr);
    const terminoRaw = this.parseFechaHoraApi(terminoStr);
    if (!inicioRaw || !terminoRaw) return null;

    const inicio = new Date(inicioRaw.getFullYear(), inicioRaw.getMonth(), inicioRaw.getDate());
    const termino = new Date(terminoRaw.getFullYear(), terminoRaw.getMonth(), terminoRaw.getDate());
    if (inicio.getTime() > termino.getTime()) return null;

    return { inicio, termino };
  }

  private clampFechaAlRangoSolucion(fechaSolo: Date, visita?: Visita): Date {
    const rango = this.getFechasPlanificacionSolucion(visita);
    if (!rango) return fechaSolo;
    if (fechaSolo.getTime() < rango.inicio.getTime()) return rango.inicio;
    if (fechaSolo.getTime() > rango.termino.getTime()) return rango.inicio;
    return fechaSolo;
  }

  private faltaRangoSolucionCompleto(): boolean {
    return this.debeAplicarRangoSolucion() && !this.getFechasPlanificacionSolucion();
  }

  private esFechaFueraDeRangoSolucion(): boolean {
    if (!this.debeAplicarRangoSolucion() || !this.fechaProgramadaISO) return false;
    const rango = this.getFechasPlanificacionSolucion();
    if (!rango) return true;

    const parteFecha = this.fechaProgramadaISO.split('T')[0];
    const [y, mo, d] = parteFecha.split('-').map(s => parseInt(s, 10) || 0);
    if (!y || !mo || !d) return true;
    const fechaSel = new Date(y, mo - 1, d);
    return fechaSel.getTime() < rango.inicio.getTime() || fechaSel.getTime() > rango.termino.getTime();
  }

  private mensajeRangoSolucionInvalido(): string {
    const visita = this.getVisitaActiva();
    const inicio = this.obtenerFechaPlanificadaDesdeVisita(visita, 'fecha_inicio_planificada');
    const termino = this.obtenerFechaPlanificadaDesdeVisita(visita, 'fecha_termino_planificada');
    if (inicio && termino) {
      return `La fecha debe estar dentro del periodo planificado de la solución (desde ${this.formatearFechaHistorialVisita(inicio)} hasta ${this.formatearFechaHistorialVisita(termino)})`;
    }
    return 'La fecha debe estar dentro del periodo planificado de la solución';
  }

  private llenarFormulario(visita: Visita): void {
    const aplicaRangoSolucion = this.esAgendarDesdePlanificada() && this.visitaTieneRangoPlanificado(visita);
    const fechaReferencia =
      this.esAgendarDesdePlanificada() && visita.fecha_inicio_planificada
        ? visita.fecha_inicio_planificada
        : visita.fecha;
    
    // Convertir fecha a formato para el formulario
    if (fechaReferencia) {
      const fecha = this.parseFechaHoraApi(fechaReferencia) ?? new Date(fechaReferencia);
      // Validar que la fecha sea válida
      if (!isNaN(fecha.getTime())) {
        let fechaSolo = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
        if (aplicaRangoSolucion) {
          fechaSolo = this.clampFechaAlRangoSolucion(fechaSolo, visita);
          this.fechaProgramadaISO = fechaSolo.toISOString();
          const horas = String(fecha.getHours()).padStart(2, '0');
          const minutos = String(fecha.getMinutes()).padStart(2, '0');
          this.horaProgramada = `${horas}:${minutos}`;
        } else {
          const ahora = new Date();
          // Si la fecha de la visita ya pasó (reagendar), usar hoy como mínima
          if (visita.tipo == "Diagnóstico"){
            let fechaDiag =  new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
            this.fechaProgramadaISO = fechaDiag.toISOString();
          }
          else if (fechaSolo.getTime() < new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate()).getTime()) {
            const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
            this.fechaProgramadaISO = hoy.toISOString();
            this.horaProgramada = '00:00';
          } else {
            this.fechaProgramadaISO = fechaSolo.toISOString();
            const horas = String(fecha.getHours()).padStart(2, '0');
            const minutos = String(fecha.getMinutes()).padStart(2, '0');
            this.horaProgramada = `${horas}:${minutos}`;
          }
        }
      } else {
        this.fechaProgramadaISO = null;
        this.horaProgramada = '00:00';
      }
    } else {
      this.fechaProgramadaISO = null;
      this.horaProgramada = '00:00';
    }

    this.formulario.patchValue({
      id_productor: visita.productor?.id_productor || '',
      lugar: visita.lugar || '',
      fecha_programada: this.fechaProgramadaISO || '',
      hora_programada: this.horaProgramada || '',
      id_tipo_visita: this.obtenerIdTipoVisita(visita.tipo) || '',
      id_objetivo_visita: extraerIdsObjetivoDesdeVisita(visita),
      objetivo_otro: visita.objetivo_otro || '',
      comentarios: visita.comentarios || '', // Cargar comentarios si vienen en los detalles
      descripcion: obtenerDescripcionVisita(visita),
      es_contingencia: Boolean(visita.es_contingencia),
      bolsa_visita: visita.bolsa_visita === true,
      descripcion_emergencia: visita.descripcion_emergencia || ''
    });
  }
  
  /**
   * Aplica las restricciones de campos según la acción actual
   */
  private aplicarRestriccionesSegunAccion(): void {
    if (this.accion === 'ver-detalles') {
      // En modo ver detalles, deshabilitar todo el formulario
      this.formulario.disable();
    } else if (this.accion === 'reagendar') {
      // En modo reagendar: solo Usuario INDAP no es editable (se muestra como texto). Resto editable.
      this.formulario.enable();
      this.formulario.get('id_productor')?.disable({ emitEvent: false });
      if (this.esAgendarDesdePlanificada()) {
        this.formulario.get('id_tipo_visita')?.disable({ emitEvent: false });
      }
      this.aplicarRestriccionBolsaVisita();
      this.aplicarValidadorDescripcion(true);
    } else if (this.accion === 'nueva') {
      this.formulario.enable();
      this.aplicarRestriccionBolsaVisita();
      this.aplicarValidadorDescripcion(true);
      const archivoAnexo = this.formulario.get('archivo_anexo')?.value;
      if (!archivoAnexo) {
        this.formulario.get('categoria_anexo')?.disable({ emitEvent: false });
      }
    } else if (this.accion === 'registrar') {
      this.formularioRegistro.enable();
      this.aplicarModalidadContactoSegunBolsa();
      if (this.fechaVisitaRegistroISO && this.horaInicioRegistro) {
        this.horaTerminoRegistro = this.obtenerHoraInicioMasUnaHora(this.horaInicioRegistro);
      } else {
        this.horaTerminoRegistro = null;
      }
    } else {
      // Cancelar u otros: habilitar campos sin descripción obligatoria
      this.formulario.enable();
      this.aplicarValidadorDescripcion(false);
      const archivoAnexo = this.formulario.get('archivo_anexo')?.value;
      if (!archivoAnexo) {
        this.formulario.get('categoria_anexo')?.disable({ emitEvent: false });
      }
    }
  }

  esDiagnostico(): boolean {
    const idTipo = this.formulario.get('id_tipo_visita')?.value;
    if (!idTipo) return false;
    const tipo = this.tiposVisita.find(t => t.id_tipo_visita === Number(idTipo));
    return tipo?.descripcion?.toLowerCase().includes('diagnóstico') || tipo?.descripcion?.toLowerCase().includes('diagnostico') || false;
  }

  private aplicarRestriccionBolsaVisita(): void {
    const ctrl = this.formulario.get('bolsa_visita');
    if (!ctrl) return;
    if (this.esDiagnostico()) {
      ctrl.setValue(false, { emitEvent: false });
      ctrl.disable({ emitEvent: false });
    } else if (this.visitaTieneRangoPlanificado(this.getVisitaActiva())) {
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  }

  private aplicarValidadorDescripcion(obligatorio: boolean): void {
    const ctrl = this.formulario.get('descripcion');
    if (!ctrl) return;
    if (obligatorio) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Obtiene el ID del tipo de visita basado en la descripción
   */
  private obtenerIdTipoVisita(descripcion: string): number | null {
    const tipo = this.tiposVisita.find(t => 
      t.descripcion.toLowerCase() === descripcion.toLowerCase()
    );
    return tipo ? tipo.id_tipo_visita : null;
  }

  /**
   * Fecha de la visita (solo día, inicio del día) en ISO. Usado como mínimo en reagendar/registrar.
   */
  private getFechaVisitaInicioDiaISO(): string | null {
    const ref =
      this.esAgendarDesdePlanificada() && this.visita?.fecha_inicio_planificada
        ? this.visita.fecha_inicio_planificada
        : this.visita?.fecha;
    if (!ref) return null;
    const d = this.parseFechaHoraApi(ref) ?? new Date(ref);
    if (isNaN(d.getTime())) return null;
    const inicio = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return inicio.toISOString();
  }

  /**
   * Fecha mínima seleccionable para Agendar/Reagendar.
   * - Agendar Planificada con solución: inicio del periodo planificado.
   * - Nueva visita: hoy (no agendar en fechas pasadas).
   * - Reagendar: fecha de la visita (no cambiar a una fecha menor a la registrada).
   */
  getFechaMinimaISO(): string {
    
    const rangoSolucion = this.getFechasPlanificacionSolucion();
    if (rangoSolucion) return this.getInicioDiaISO(rangoSolucion.inicio);

    /*if (this.accion === 'reagendar') {
      const minVisita = this.getFechaVisitaInicioDiaISO();
      if (minVisita) return minVisita;
    }
    const hoy = new Date();
    const inicioHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    return inicioHoy.toISOString();*/

    if(this.visitaDiagnostico){
      const fecha = new Date(this.visitaDiagnostico?.fecha_visita??"");
      if (isNaN(fecha.getTime())) return "";
      return fecha.toISOString();
    }
    if (this.esDiagnostico()) {
      return new Date(2000, 0, 1).toISOString(); // Permite seleccionar fechas pasadas
    }

    const hoy = new Date();
    const inicioHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    return inicioHoy.toISOString();

  }

  /**
   * Fecha máxima seleccionable al Agendar visitas Planificadas vinculadas a una solución.
   */
  getFechaMaximaISO(): string | undefined {
    const rangoSolucion = this.getFechasPlanificacionSolucion();
    if (!rangoSolucion) return undefined;
    return this.getInicioDiaISO(rangoSolucion.termino);
  }

  /**
   * Fecha mínima para "Fecha de realización" en Registrar Visita.
   * No se puede registrar con una fecha menor a la fecha programada de la visita.
   */
  getFechaMinimaRegistroISO(): string {
    const minVisita = this.getFechaVisitaInicioDiaISO();
    if (minVisita) return minVisita;
    const hoy = new Date();
    const inicioHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    return inicioHoy.toISOString();
  }

  /**
   * Valor para el ion-datetime de Hora término (fecha + hora en hora local, sin UTC).
   * Usar hora local evita que el picker muestre 4h más por conversión toISOString().
   */
  getHoraTerminoRegistroValueISO(): string | undefined {
    if (!this.fechaVisitaRegistroISO || !this.horaTerminoRegistro) return undefined;
    const parteFecha = this.fechaVisitaRegistroISO.split('T')[0];
    return `${parteFecha}T${this.horaTerminoRegistro}:00.000`;
  }

  /**
   * Retorna la hora "HH:mm" resultante de sumar 1 hora a una hora dada en formato "HH:mm".
   */
  private obtenerHoraInicioMasUnaHora(horaHHmm: string): string {
    const [h, m] = horaHHmm.split(':').map(s => parseInt(s, 10) || 0);
    const d = new Date(0, 0, 0, h, m, 0, 0);
    d.setHours(d.getHours() + 1);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  /**
   * Hora mínima para "Hora término" en Registrar Visita: 1 hora después de "Hora inicio".
   * Retorna fecha-hora en hora local (sin Z) para que el picker no sume el offset UTC.
   */
  getHoraTerminoMinimaRegistroISO(): string | undefined {
    if (!this.fechaVisitaRegistroISO || !this.horaInicioRegistro) return undefined;
    const parteFecha = this.fechaVisitaRegistroISO.split('T')[0];
    const horaMin = this.obtenerHoraInicioMasUnaHora(this.horaInicioRegistro);
    return `${parteFecha}T${horaMin}:00.000`;
  }

  /**
   * Compara solo la parte fecha (yyyy-mm-dd) de dos cadenas ISO.
   * Retorna true si fechaISO es estrictamente anterior a fechaReferenciaISO.
   */
  private esFechaAnteriorA(fechaISO: string, fechaReferenciaISO: string): boolean {
    const a = fechaISO.split('T')[0];
    const b = fechaReferenciaISO.split('T')[0];
    return a < b;
  }

  /**
   * Indica si la fecha+hora seleccionada (fechaISO + hora HH:MM) es anterior a la fecha/hora de referencia (ISO).
   * Usado en Reagendar: no permitir cambiar a una fecha/hora anterior a la actualmente programada.
   * Construye la fecha seleccionada en hora local para evitar problemas de zona horaria.
   * @param truncarRefAMinutos si true, la referencia se trunca a minutos (para Registrar: 12:41:01 ≈ 12:41)
   */
  private esFechaHoraAnteriorA(
    fechaISO: string,
    hora: string,
    fechaReferenciaISO: string,
    truncarRefAMinutos = false
  ): boolean {
    const parteFecha = fechaISO.split('T')[0];
    const [y, mo, d] = parteFecha.split('-').map(s => parseInt(s, 10) || 0);
    const partesHora = hora.split(':').map(s => parseInt(s, 10) || 0);
    const hr = partesHora[0] ?? 0;
    const min = partesHora[1] ?? 0;
    const fechaSel = new Date(y, mo - 1, d, hr, min, 0, 0);
    let fechaRef = new Date(fechaReferenciaISO);
    if (truncarRefAMinutos) {
      fechaRef = new Date(
        fechaRef.getFullYear(),
        fechaRef.getMonth(),
        fechaRef.getDate(),
        fechaRef.getHours(),
        fechaRef.getMinutes(),
        0,
        0
      );
    }
    if (isNaN(fechaSel.getTime()) || isNaN(fechaRef.getTime())) return false;
    return fechaSel.getTime() < fechaRef.getTime();
  }

  /**
   * Indica si la fecha y hora seleccionadas están en el pasado.
   * En nueva/reagendar la hora es opcional: por defecto queda 00:00. Esa medianoche del mismo día
   * no debe invalidar el formulario durante el día (solo importa que la fecha no sea anterior a hoy).
   * Si el usuario elige otra hora, se compara fecha+hora completas.
   */
  private esFechaHoraEnPasado(): boolean {
    if (this.esDiagnostico()) return false;
    if (this.debeAplicarRangoSolucion()) return false;
    if (!this.fechaProgramadaISO) return false;

    const horaRaw = (this.horaProgramada ?? '').trim();
    const sinHoraExplicita = horaRaw === '' || horaRaw === '00:00';

    if ((this.accion === 'nueva' || this.accion === 'reagendar') && sinHoraExplicita) {
      const parteFecha = this.fechaProgramadaISO.split('T')[0];
      const [y, mo, d] = parteFecha.split('-').map(s => parseInt(s, 10) || 0);
      if (!y || !mo || !d) return false;
      const inicioDiaSel = new Date(y, mo - 1, d, 0, 0, 0, 0);
      const ahora = new Date();
      const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), 0, 0, 0, 0);
      return inicioDiaSel.getTime() < inicioHoy.getTime();
    }

    if (!this.horaProgramada) return false;
    const [h, m] = this.horaProgramada.split(':');
    const fecha = new Date(this.fechaProgramadaISO);
    fecha.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
    return fecha.getTime() < Date.now();
  }

  /**
   * Maneja el cambio de fecha
   */
  onFechaChange(event: any): void {
    const fechaISO = event.detail.value;
    if (fechaISO) {
      this.fechaProgramadaISO = fechaISO;
      this.formulario.patchValue({ fecha_programada: fechaISO });
    } else {
      this.fechaProgramadaISO = null;
      this.formulario.patchValue({ fecha_programada: '' });
    }
    this.cdr.markForCheck();
  }

  /**
   * Maneja el cambio de hora
   */
  onHoraChange(event: any): void {
    const valor = event.detail.value;
    if (valor) {
      // ion-datetime puede devolver un ISO string completo o solo la hora
      // Extraer solo la parte de hora (HH:MM)
      let hora: string;
      if (valor.includes('T')) {
        // Es un ISO string, extraer la hora
        const fecha = new Date(valor);
        const horas = String(fecha.getHours()).padStart(2, '0');
        const minutos = String(fecha.getMinutes()).padStart(2, '0');
        hora = `${horas}:${minutos}`;
      } else {
        // Ya es formato HH:MM
        hora = valor;
      }
      
      this.horaProgramada = hora;
      this.formulario.patchValue({ hora_programada: hora });
    } else {
      this.horaProgramada = '00:00';
      this.formulario.patchValue({ hora_programada: '00:00' });
    }
    this.cdr.markForCheck();
  }

  /**
   * Maneja la selección de archivo
   */
  onArchivoSeleccionado(event: any): void {
    const input = event.target as HTMLInputElement;
    const archivo = input.files?.[0];
    if (archivo) {
      const check = validateUploadFile(archivo);
      if (!check.valid) {
        this.toastService.mostrar(check.message, 'warning');
        input.value = '';
        this.formulario.patchValue({ archivo_anexo: null });
        this.formulario.get('categoria_anexo')?.disable({ emitEvent: false });
        this.formulario.patchValue({ categoria_anexo: '' });
        return;
      }
      this.formulario.patchValue({ archivo_anexo: archivo });
      // Habilitar el campo de categoría del anexo cuando hay un archivo seleccionado
      this.formulario.get('categoria_anexo')?.enable({ emitEvent: false });
    } else {
      // Si no hay archivo, deshabilitar y limpiar el campo de categoría
      this.formulario.get('categoria_anexo')?.disable({ emitEvent: false });
      this.formulario.patchValue({ categoria_anexo: '' });
    }
  }

  /** Cambio de fecha de realización (registrar). */
  onFechaRegistroChange(event: any): void {
    const v = event.detail?.value;
    this.fechaVisitaRegistroISO = v || null;
    this.cdr.markForCheck();
  }

  /** Cambio de hora de inicio (registrar). */
  onHoraInicioRegistroChange(event: any): void {
    const valor = event.detail?.value;
    if (valor) {
      const hora = valor.includes('T') ? this.extraerHoraDeISO(valor) : valor;
      this.horaInicioRegistro = hora;
      const minTermino = this.getHoraTerminoMinimaRegistroISO();
      if (minTermino) {
        const horaMinTermino = this.extraerHoraDeISO(minTermino);
        if (!this.horaTerminoRegistro || this.esHoraAnteriorA(this.horaTerminoRegistro, horaMinTermino)) {
          this.horaTerminoRegistro = horaMinTermino;
        }
      }
    } else {
      this.horaInicioRegistro = null;
    }
    this.cdr.markForCheck();
  }

  /** Retorna true si la hora a (HH:mm) es estrictamente anterior a la hora b (HH:mm). */
  private esHoraAnteriorA(a: string, b: string): boolean {
    const [ha, ma] = a.split(':').map(s => parseInt(s, 10) || 0);
    const [hb, mb] = b.split(':').map(s => parseInt(s, 10) || 0);
    return ha < hb || (ha === hb && ma < mb);
  }

  /** Cambio de hora de término (registrar). */
  onHoraTerminoRegistroChange(event: any): void {
    const valor = event.detail?.value;
    if (valor) {
      const hora = valor.includes('T') ? this.extraerHoraDeISO(valor) : valor;
      this.horaTerminoRegistro = hora;
    } else {
      this.horaTerminoRegistro = null;
    }
    this.cdr.markForCheck();
  }

  private extraerHoraDeISO(iso: string): string {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  /** Añade una nueva fila de archivo + categoría. Hace scroll para que la nueva fila sea visible. */
  agregarFilaArchivoRegistro(): void {
    this.itemsRegistroArchivos = [...this.itemsRegistroArchivos, { file: null, id_categoria: null }];
    this.cdr.markForCheck();
    setTimeout(() => this.scrollToUltimaFilaAdjunto(), 80);
  }

  /** Hace scroll hasta el final del contenido del panel (tras agregar archivo). */
  private scrollToUltimaFilaAdjunto(): void {
    const el = this.formularioContentRef?.nativeElement;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }

  /** Elimina la fila de archivo en el índice dado. Puede quedar sin filas. */
  eliminarFilaArchivoRegistro(index: number): void {
    this.itemsRegistroArchivos = this.itemsRegistroArchivos.filter((_, i) => i !== index);
    this.cdr.markForCheck();
  }

  /** true si hay al menos un archivo nuevo seleccionado sin categoría (Registrar Visita). */
  hayAdjuntoRegistroSinCategoria(): boolean {
    return (this.itemsRegistroArchivos ?? []).some(
      (it) => !it?.adjuntoGuardado && it?.file != null && it?.id_categoria == null
    );
  }

  /** Al menos un adjunto completo (nuevo o ya guardado), requerido para tipo_reporte papel. */
  hayAdjuntoRegistroCompleto(): boolean {
    return (this.itemsRegistroArchivos ?? []).some(
      (it) =>
        !!it?.adjuntoGuardado ||
        (it?.file != null && it?.id_categoria != null)
    );
  }

  /** Asigna el archivo seleccionado a la fila en el índice dado. */
  onArchivoRegistroFilaChange(index: number, event: Event): void {
    const input = event?.target as HTMLInputElement;
    const raw = input?.files?.[0] ?? null;
    if (raw) {
      const check = validateUploadFile(raw);
      if (!check.valid) {
        this.toastService.mostrar(check.message, 'warning');
        input.value = '';
        return;
      }
    }
    const file = raw;
    if (index >= 0 && index < this.itemsRegistroArchivos.length) {
      this.itemsRegistroArchivos = this.itemsRegistroArchivos.map((item, i) =>
        // Si se limpia el archivo, limpiar también la categoría para no dejar selección “huérfana”.
        i === index ? { ...item, file, ...(file == null ? { id_categoria: null } : {}) } : item
      );
    }
    this.cdr.markForCheck();
  }

  /** Asigna la categoría seleccionada a la fila en el índice dado. */
  onCategoriaRegistroFilaChange(index: number, value: number | string | null): void {
    const id = value != null && value !== '' ? Number(value) : null;
    if (index >= 0 && index < this.itemsRegistroArchivos.length) {
      this.itemsRegistroArchivos = this.itemsRegistroArchivos.map((item, i) =>
        i === index ? { ...item, id_categoria: id } : item
      );
    }
    this.cdr.markForCheck();
  }

  /** Nombre del archivo para mostrar en la fila (o texto por defecto). */
  nombreArchivoRegistroFila(index: number): string {
    const item = this.itemsRegistroArchivos[index];
    if (item?.adjuntoGuardado?.nombre_archivo?.trim()) {
      return item.adjuntoGuardado.nombre_archivo.trim();
    }
    return item?.file?.name ?? 'Seleccionar archivo';
  }

  /**
   * Maneja touchend en el overlay para cerrar al primer toque (evita que el backdrop no responda en Registrar).
   * preventDefault evita el click sintético posterior y doble cierre.
   */
  onOverlayTouchend(event: TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.cerrar();
  }

  /**
   * Sin red el agendamiento vive en Ruta del día (IndexedDB), no en este formulario HTTP.
   */
  private async desviarAgendarAFormularioOffline(): Promise<void> {
    await this.toastService.mostrar(
      'Sin conexión. Complete el agendamiento en Ruta del día.',
      'warning',
      2800
    );
    this.cerrar();
    const ruta = this.router.url.split('?')[0];
    if (ruta !== '/ruta-del-dia' && !ruta.startsWith('/ruta-del-dia/')) {
      await this.router.navigate(['/ruta-del-dia'], { replaceUrl: true });
    }
    this.visitaOfflinePanel.abrir();
  }

  /**
   * Cierra el formulario
   */
  cerrar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.visitaDetalle = null;
    this.limpiarEstadoHistorialPanel();
    this.formulario.reset({
      id_productor: '',
      lugar: '',
      fecha_programada: '',
      hora_programada: '',
      id_tipo_visita: '',
      id_objetivo_visita: [],
      comentarios: '',
      descripcion: '',
      descripcion_emergencia: '',
      archivo_anexo: null,
      categoria_anexo: '',
      es_contingencia: null,
      bolsa_visita: false
    });
    this.formulario.enable();
    this.formularioRegistro.reset({
      tipo_reporte: null,
      id_modalidad_contacto: null,
      id_objetivo_visita: [],
      objetivo_otro: '',
      generar_folio: false,
      folio: '',
      recomendaciones: '',
      compromisos: ''
    });
    this.aplicarValidadoresRegistroPorTipo(null);
    // Deshabilitar categoría del anexo por defecto
    this.formulario.get('categoria_anexo')?.disable({ emitEvent: false });
    // Limpiar fechas y horas
    this.fechaProgramadaISO = null;
    this.horaProgramada = null;
    this.fechaVisitaRegistroISO = null;
    this.horaInicioRegistro = null;
    this.horaTerminoRegistro = null;
    this.itemsRegistroArchivos = [];
    // Limpiar búsqueda de productores
    this.terminoBusquedaProductor = '';
  }

  /**
   * Obtiene el título del formulario según la acción
   */
  getTitulo(): string {
    switch (this.accion) {
      case 'nueva':
        return 'Agendar Nueva Visita';
      case 'reagendar':
        return this.esAgendarDesdePlanificada() ? 'Agendar' : 'Reagendar Visita';
      case 'cancelar':
        return 'Cancelar Visita';
      case 'registrar':
        return 'Finalizar Visita';
      case 'ver-detalles':
        return 'Ver Detalles de la Visita';
      default:
        return 'Gestionar Visita';
    }
  }

  /**
   * Obtiene el texto del botón de acción principal
   */
  getTextoBotonGuardar(): string {
    switch (this.accion) {
      case 'nueva':
        return 'Agendar Visita';
      case 'reagendar':
        return this.esAgendarDesdePlanificada() ? 'Agendar' : 'Reagendar';
      case 'cancelar':
        return 'Cancelar Visita';
      case 'registrar':
        return 'Finalizar Visita';
      default:
        return 'Guardar';
    }
  }

  /**
   * Verifica si el formulario está en modo solo lectura
   */
  esModoSoloLectura(): boolean {
    return this.accion === 'ver-detalles';
  }

  /**
   * Verifica si el campo de Usuario INDAP debe estar deshabilitado
   * (en reagendar se muestra como texto estático; en ver-detalles todo está deshabilitado)
   */
  esUsuarioDeshabilitado(): boolean {
    return this.accion === 'reagendar' || this.esModoSoloLectura();
  }

  /**
   * Verifica si un campo debe estar deshabilitado.
   * Solo en ver-detalles los campos están deshabilitados.
   * En reagendar, todo es editable excepto Usuario INDAP (que se muestra como texto).
   */
  esCampoDeshabilitado(): boolean {
    return this.esModoSoloLectura();
  }

  /** Permite editar siempre la descripción al agendar visitas planificadas o reagendar. */
  esDescripcionDeshabilitada(): boolean {
    return false;
  }

  /** Bolsa de Visita bloqueada cuando la visita tiene rango planificado. */
  esBolsaVisitaDeshabilitada(): boolean {
    return this.esCampoDeshabilitado() || this.visitaTieneRangoPlanificado(this.getVisitaActiva());
  }

  /**
   * Nombre completo del productor: nombre + primer_apellido + segundo_apellido.
   * Compatible con API que devuelve los campos por separado.
   */
  getNombreCompletoProductor(productor: Productor | null | undefined): string {
    if (!productor) return '';
    const partes = [productor.nombre, productor.primer_apellido, productor.segundo_apellido].filter(Boolean);
    return partes.length ? partes.join(' ') : (productor.nombre || '');
  }

  /**
   * Devuelve el valor a mostrar o 'S/I' si está vacío. Usado en la vista Ver Detalles.
   * Considera vacíos: null, undefined, string vacío, "No especificado" y "No especificada".
   */
  valorOSinInfo(val: string | null | undefined): string {
    if (val == null || val === undefined) return 'S/I';
    const s = String(val).trim();
    if (s === '' || s === 'No especificado' || s === 'No especificada') return 'S/I';
    return s;
  }

  /**
   * Nombre del Usuario INDAP para mostrar en modo reagendar (texto estático).
   * Muestra nombre + primer_apellido + segundo_apellido.
   */
  getNombreProductorReagendar(): string {
    if (this.visita?.productor) {
      const completo = this.getNombreCompletoProductor(this.visita.productor);
      if (completo) return completo;
    }
    const idProductor = this.formulario.get('id_productor')?.value;
    return this.getNombreProductor(idProductor || '') || '—';
  }

  /**
   * Verifica si se deben mostrar todos los campos del formulario (nueva/reagendar)
   */
  mostrarCamposCompletos(): boolean {
    return this.accion === 'nueva' || this.accion === 'reagendar';
  }

  /** true cuando la acción es registrar (formulario de registro: fecha/hora, tipo de reporte, detalle, adjuntos). */
  esAccionRegistrar(): boolean {
    return this.accion === 'registrar';
  }

  /** Valor actual de tipo de reporte en el formulario de registrar (digital / papel / sin elegir). */
  getTipoReporteRegistro(): 'digital' | 'papel' | null {
    const v = this.formularioRegistro.get('tipo_reporte')?.value;
    return v === 'digital' || v === 'papel' ? v : null;
  }

  onFolioInput(event: CustomEvent): void {
    if (!this.esFolioRegistroEditable()) return;
    aplicarEntradaFolio(this.formularioRegistro.get('folio'), event.detail?.value as string);
  }

  onGenerarFolioRegistroChange(event: CustomEvent): void {
    const checked = event.detail?.checked === true;
    this.formularioRegistro.get('generar_folio')?.setValue(checked, { emitEvent: true });
    this.aplicarValidadoresFolioRegistro();
    this.cdr.markForCheck();
  }

  /**
   * Comparadores como arrow functions: ion-select invoca compareWith sin contexto `this`.
   */
  readonly compareIdModalidadContacto = (
    o1: number | string | null | undefined,
    o2: number | string | null | undefined
  ): boolean => {
    if (o1 == null && o2 == null) return true;
    if (o1 == null || o2 == null || o1 === '' || o2 === '') return false;
    return Number(o1) === Number(o2);
  };

  textoObjetivosHistorial(visita: Visita): string {
    return formatearObjetivosVisita(visita, this.objetivosVisita);
  }

  listaObjetivosHistorial(visita: Visita): string[] {
    const texto = formatearObjetivosVisita(visita, this.objetivosVisita);
    if (!texto) return [];
    return texto.split('; ').filter(Boolean);
  }

  textoModalidadContactoHistorial(visita: Visita): string {
    return formatearModalidadContacto(visita, this.modalidadesContacto);
  }

  esModalidadContactoRegistroEditable(): boolean {
    return this.getVisitaActiva()?.bolsa_visita === true;
  }

  /**
   * Verifica si el campo de categoría del anexo debe estar deshabilitado
   * Está deshabilitado si:
   * - El campo general está deshabilitado (modo reagendar o solo lectura)
   * - O si no hay archivo seleccionado
   */
  esCategoriaAnexoDeshabilitado(): boolean {
    if (this.esCampoDeshabilitado()) {
      return true;
    }
    const archivoAnexo = this.formulario.get('archivo_anexo')?.value;
    return !archivoAnexo;
  }

  mostrarCampoDescripcionEmergencia(): boolean {
    if (!(this.accion === 'nueva' || this.accion === 'reagendar')) {
      return false;
    }
    return this.formulario.get('es_contingencia')?.value === true;
  }

  /**
   * Verifica si el formulario es válido (incluyendo fecha y hora).
   * Usa getRawValue() para incluir valores de controles deshabilitados (modo reagendar).
   */
  esFormularioValido(): boolean {
    if (this.esModoSoloLectura()) return true;

    if (this.accion === 'registrar') {
      const fechaReg = this.fechaVisitaRegistroISO;
      const horaInicio = this.horaInicioRegistro;
      const tieneFechaHora = !!fechaReg && !!horaInicio;
      if (!tieneFechaHora) return false;
      if (this.hayAdjuntoRegistroSinCategoria()) return false;
      if (!this.formularioRegistro.valid) return false;
      const tipo = this.formularioRegistro.get('tipo_reporte')?.value as 'digital' | 'papel' | null;
      if (tipo === 'papel' && !this.hayAdjuntoRegistroCompleto()) return false;
      // No permitir fecha/hora de realización anterior a la programada. Se compara por minuto
      // para que al prellenar con la hora de la visita (ej. 12:41) no quede invalidado por segundos (12:41:01).
      const esRegistroFechaHoraAnterior =
        !!this.visita?.fecha &&
        this.esFechaHoraAnteriorA(fechaReg, horaInicio, this.visita.fecha, true);
      return !esRegistroFechaHoraAnterior;
    }

    const raw = this.formulario.getRawValue();
    const idProductor = raw.id_productor;
    const lugar = raw.lugar;
    const tipoVisita = raw.id_tipo_visita;

    const idProductorValido = idProductor != null && String(idProductor).trim() !== '';
    const lugarValido = lugar != null && String(lugar).trim() !== '';
    const tipoVisitaValido = tipoVisita != null && (tipoVisita !== '' || typeof tipoVisita === 'number');
    const emergenciaValida = raw.es_contingencia === true || raw.es_contingencia === false;
    const descripcionValida =
      this.accion !== 'nueva' && this.accion !== 'reagendar'
        ? true
        : String(raw.descripcion ?? '').trim().length > 0;
    const camposValidos =
      idProductorValido && lugarValido && tipoVisitaValido && emergenciaValida && descripcionValida && this.formulario.valid;

    const fechaHoraValidas = !!this.fechaProgramadaISO;
    const noEsPasado = (this.accion !== 'nueva' && this.accion !== 'reagendar') || !this.esFechaHoraEnPasado();
    const rangoSolucionValido = !this.faltaRangoSolucionCompleto() && !this.esFechaFueraDeRangoSolucion();

    // Reagendar: no permitir fecha/hora anterior a la actualmente programada (excepto Agendar con solución)
    const fechaProgramadaVisita =
      this.esAgendarDesdePlanificada() && this.visita?.fecha_inicio_planificada
        ? this.visita.fecha_inicio_planificada
        : this.visita?.fecha;
    const esReagendarFechaAnterior =
      !this.esDiagnostico() &&
      !this.debeAplicarRangoSolucion() &&
      this.accion === 'reagendar' &&
      !!fechaProgramadaVisita &&
      !!this.fechaProgramadaISO &&
      this.esFechaHoraAnteriorA(
        this.fechaProgramadaISO,
        this.horaProgramada || '00:00',
        fechaProgramadaVisita
      );
    const noEsReagendarAnterior = !esReagendarFechaAnterior;

    return camposValidos && fechaHoraValidas && noEsPasado && noEsReagendarAnterior && rangoSolucionValido;
  }

  /**
   * Guarda el formulario según la acción
   */
  async guardar(): Promise<void> {
    if (this.accion === 'nueva' && !this.networkService.isOnline()) {
      await this.desviarAgendarAFormularioOffline();
      return;
    }

    if (!this.esFormularioValido() && !this.esModoSoloLectura()) {
      if (this.accion === 'registrar' && this.hayAdjuntoRegistroSinCategoria()) {
        this.toastService.mostrar('Seleccione una categoría para cada archivo adjunto.', 'warning');
        return;
      }
      if (
        this.accion === 'registrar' &&
        this.formularioRegistro.get('tipo_reporte')?.value === 'papel' &&
        !this.hayAdjuntoRegistroCompleto()
      ) {
        this.toastService.mostrar('Adjunte al menos un archivo con su categoría.', 'warning');
        return;
      }
      this.toastService.mostrar('Por favor complete todos los campos requeridos', 'warning');
      return;
    }

    if ((this.accion === 'nueva' || this.accion === 'reagendar') && this.esFechaHoraEnPasado()) {
      this.toastService.mostrar('No se puede agendar para una fecha u hora pasada', 'warning');
      return;
    }

    if (this.debeAplicarRangoSolucion()) {
      if (this.faltaRangoSolucionCompleto()) {
        this.toastService.mostrar(this.mensajeRangoSolucionInvalido(), 'warning');
        return;
      }
      if (this.esFechaFueraDeRangoSolucion()) {
        this.toastService.mostrar(this.mensajeRangoSolucionInvalido(), 'warning');
        return;
      }
    }

    const fechaProgramadaVisitaGuardar =
      this.esAgendarDesdePlanificada() && this.visita?.fecha_inicio_planificada
        ? this.visita.fecha_inicio_planificada
        : this.visita?.fecha;
    if (
      !this.debeAplicarRangoSolucion() &&
      this.accion === 'reagendar' &&
      fechaProgramadaVisitaGuardar &&
      this.fechaProgramadaISO
    ) {
      if (
        this.esFechaHoraAnteriorA(
          this.fechaProgramadaISO,
          this.horaProgramada || '00:00',
          fechaProgramadaVisitaGuardar
        )
      ) {
        this.toastService.mostrar('No se puede reagendar a una fecha u hora anterior a la actualmente programada', 'warning');
        return;
      }
    }

    if (this.accion === 'registrar' && this.visita?.fecha && this.fechaVisitaRegistroISO && this.horaInicioRegistro) {
      if (this.esFechaHoraAnteriorA(this.fechaVisitaRegistroISO, this.horaInicioRegistro, this.visita.fecha, true)) {
        this.toastService.mostrar('La fecha y hora de realización no pueden ser anteriores a la fecha y hora programadas de la visita', 'warning');
        return;
      }
    }

    this.guardando = true;

    try {
      const datosFormulario = this.formulario.value;
      
      // Preparar datos según la acción
      switch (this.accion) {
        case 'nueva':
          await this.crearVisita(datosFormulario);
          break;
        case 'reagendar':
          if (this.visita?.id_visita) {
            await this.reagendarVisita(this.visita.id_visita, datosFormulario);
          }
          break;
        case 'cancelar':
          if (this.visita?.id_visita) {
            await this.cancelarVisita(this.visita.id_visita);
          }
          break;
        case 'registrar':
          if (this.visita?.id_visita) {
            await this.registrarVisita(this.visita.id_visita, false);
          }
          break;
      }

      this.toastService.mostrar('Operación realizada correctamente', 'success');
      this.guardado.emit();
      this.cerrar();
    } catch (error: any) {
      console.error('Error al guardar:', error);
      // Si es respuesta 200 con estado nok, el interceptor ya mostró el toast; solo evitar mensaje genérico
      if (error?.estado === 'nok') {
        return;
      }
      // 413: el archivo adjunto supera el tamaño máximo permitido por el servidor
      if (error?.status === 413) {
        this.toastService.mostrar(
          'El archivo adjunto es demasiado grande. Por favor reduzca su tamaño e inténtelo nuevamente.',
          'error'
        );
        return;
      }
      const detailApi =
        typeof error?.error?.detail === 'string'
          ? error.error.detail
          : Array.isArray(error?.error?.detail)
            ? error.error.detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join('. ')
            : null;
      const mensaje =
        detailApi ??
        error?.error?.message ??
        (typeof error?.error === 'string' ? error.error : null) ??
        error?.message ??
        'Error al realizar la operación';
      this.toastService.mostrar(mensaje, 'error');
    } finally {
      this.guardando = false;
    }
  }

  /**
   * Crea una nueva visita.
   * El endpoint POST /api/v1/visita/crear solo acepta application/json (CrearVisitaRequest).
   * Los adjuntos se envían al registrar la visita (POST /visita/{id}/registrar).
   */
  private async crearVisita(datos: any): Promise<void> {
    if (!this.networkService.isOnline()) {
      await this.desviarAgendarAFormularioOffline();
      return;
    }

    if (!this.fechaProgramadaISO) {
      throw new Error('La fecha programada es requerida');
    }

    const fechaHora = this.formatearFechaHora(this.fechaProgramadaISO, this.horaProgramada || '00:00');

    const payload = anexarObjetivosVisitaAPayloadJson(
      {
        id_productor: String(datos.id_productor).trim(),
        lugar: String(datos.lugar ?? '').trim(),
        fecha: fechaHora,
        id_tipo_visita: Number(datos.id_tipo_visita),
        comentarios: datos.comentarios != null ? String(datos.comentarios) : '',
        es_contingencia: Boolean(datos.es_contingencia),
        bolsa_visita: Boolean(datos.bolsa_visita),
        descripcion: String(datos.descripcion ?? '').trim(),
        descripcion_emergencia:
          Boolean(datos.es_contingencia) && String(datos.descripcion_emergencia ?? '').trim() !== ''
            ? String(datos.descripcion_emergencia).trim()
            : null
      },
      datos.id_objetivo_visita as number[] | number | null | undefined,
      datos.objetivo_otro as string | null | undefined
    );

    try {
      const productorInfo = await firstValueFrom(this.negocioService.getProductorNegocioPorId(payload.id_productor));
      if (productorInfo?.plan_negocio?.id_estado_plan_negocio === 3 && productorInfo.plan_negocio.id_plan_negocio) {
        (payload as any).id_plan_negocio = String(productorInfo.plan_negocio.id_plan_negocio);
      }
    } catch (e) {
      console.warn("No se pudo obtener el plan de negocio para vincular la visita", e);
    }

    const validation = this.validationService.validate(CrearVisitaRequestSchema, payload);
    if (!validation.success) {
      const mensajesError = validation.errorMessages?.join(', ') || 'Error de validación';
      throw new Error(mensajesError);
    }

    return new Promise((resolve, reject) => {
      this.visitasService.crearVisita(validation.data!).subscribe({
        next: (body: any) => {
          if (body?.estado === 'nok') {
            reject(body);
          } else {
            resolve();
          }
        },
        error: (error) => reject(error)
      });
    });
  }

  /**
   * Reagenda una visita (fecha/hora, lugar, tipo, emergencia, comentarios).
   */
  private async reagendarVisita(idVisita: string, datos: any): Promise<void> {
    if (!this.fechaProgramadaISO) {
      throw new Error('La fecha programada es requerida');
    }

    const fechaHora = this.formatearFechaHora(this.fechaProgramadaISO, this.horaProgramada || '00:00');
    const raw = this.formulario.getRawValue();

    const payload = anexarObjetivosVisitaAPayloadJson(
      {
        fecha: fechaHora,
        lugar: raw.lugar != null && String(raw.lugar).trim() !== '' ? String(raw.lugar).trim() : null,
        id_tipo_visita: raw.id_tipo_visita != null ? Number(raw.id_tipo_visita) : null,
        es_contingencia: Boolean(raw.es_contingencia),
        bolsa_visita: Boolean(raw.bolsa_visita),
        descripcion: String(raw.descripcion ?? '').trim(),
        descripcion_emergencia:
          Boolean(raw.es_contingencia) && String(raw.descripcion_emergencia ?? '').trim() !== ''
            ? String(raw.descripcion_emergencia).trim()
            : null
      },
      raw.id_objetivo_visita as number[] | number | null | undefined,
      raw.objetivo_otro as string | null | undefined
    );

    const validation = this.validationService.validate(AgendarVisitaRequestSchema, payload);
    if (!validation.success) {
      const mensajesError = validation.errorMessages?.join(', ') || 'Error de validación';
      throw new Error(mensajesError);
    }

    return new Promise((resolve, reject) => {
      this.visitasService.reagendarVisita(idVisita, validation.data!).subscribe({
        next: (body: any) => {
          if (body?.estado === 'nok') {
            reject(body);
          } else {
            resolve();
          }
        },
        error: (error) => reject(error)
      });
    });
  }

  /**
   * Cancela una visita
   */
  private async cancelarVisita(idVisita: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.visitasService.cancelarVisita(idVisita).subscribe({
        next: (body: any) => {
          if (body?.estado === 'nok') {
            reject(body);
          } else {
            resolve();
          }
        },
        error: (error) => reject(error)
      });
    });
  }

  /**
   * Guarda el progreso parcial del registro (POST /visita/{id}/registrar con es_parcial=true).
   */
  async guardarProgreso(): Promise<void> {
    if (!this.visita?.id_visita) return;

    this.guardando = true;
    try {
      await this.registrarVisita(this.visita.id_visita, true);
      this.toastService.mostrar('Progreso guardado correctamente', 'success');
      this.guardado.emit();
      this.cerrar();
    } catch (error: any) {
      console.error('Error al guardar progreso:', error);
      if (error?.estado === 'nok') {
        return;
      }
      // 413: el archivo adjunto supera el tamaño máximo permitido por el servidor
      if (error?.status === 413) {
        this.toastService.mostrar(
          'El archivo adjunto es demasiado grande. Por favor reduzca su tamaño e inténtelo nuevamente.',
          'error'
        );
        return;
      }
      const detailApi =
        typeof error?.error?.detail === 'string'
          ? error.error.detail
          : Array.isArray(error?.error?.detail)
            ? error.error.detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join('. ')
            : null;
      const mensaje =
        detailApi ??
        error?.error?.message ??
        (typeof error?.error === 'string' ? error.error : null) ??
        error?.message ??
        'Error al guardar el progreso';
      this.toastService.mostrar(mensaje, 'error');
    } finally {
      this.guardando = false;
    }
  }

  /**
   * Registra una visita (POST /visita/{id}/registrar con multipart: fecha_visita, hora_inicio, etc.)
   */
  private async registrarVisita(idVisita: string, esParcial: boolean): Promise<void> {
    const formData = await this.construirFormDataRegistro(esParcial);

    return new Promise((resolve, reject) => {
      this.visitasService.registrarVisita(idVisita, formData).subscribe({
        next: (body: any) => {
          if (body?.estado === 'nok') {
            reject(body);
          } else {
            resolve();
          }
        },
        error: (error) => reject(error)
      });
    });
  }

  /** Construye FormData para POST /visita/{id}/registrar (multipart, campos según OpenAPI). */
  private async construirFormDataRegistro(esParcial: boolean): Promise<FormData> {
    const formData = new FormData();
    formData.append('es_parcial', String(esParcial));

    if (this.fechaVisitaRegistroISO) {
      formData.append('fecha_visita', this.formatearFechaVisitaYYYYMMDD(this.fechaVisitaRegistroISO));
    }
    if (this.horaInicioRegistro) {
      formData.append('hora_inicio', this.horaInicioRegistro);
    }
    if (this.horaTerminoRegistro) {
      formData.append('hora_termino', this.horaTerminoRegistro);
    }

    const reg = this.formularioRegistro.getRawValue();
    const tipoReporte = String(reg.tipo_reporte ?? '').trim();
    if (tipoReporte === 'digital' || tipoReporte === 'papel') {
      formData.append('tipo_reporte', tipoReporte);
    }
    appendObjetivosVisitaAFormData(
      formData,
      normalizarIdsObjetivoVisita(reg.id_objetivo_visita as number[] | number | null),
      reg.objetivo_otro
    );
    if (reg.id_modalidad_contacto != null && reg.id_modalidad_contacto !== '') {
      formData.append('id_modalidad_contacto', String(reg.id_modalidad_contacto));
    }
    const generarFolio = reg.generar_folio === true;
    appendGenerarFolioAFormData(formData, generarFolio);
    if (!generarFolio) {
      const folio = normalizarFolioInput(reg.folio);
      if (folio) {
        formData.append('folio', folio);
      }
    }
    const recomendaciones = String(reg.recomendaciones ?? '').trim();
    if (recomendaciones) {
      formData.append('recomendaciones', recomendaciones);
    }
    const compromisos = String(reg.compromisos ?? '').trim();
    if (compromisos) {
      formData.append('compromisos', compromisos);
    }

    await this.agregarArchivosRegistroAFormData(formData, esParcial);

    return formData;
  }

  /**
   * Incluye archivos en multipart: nuevos siempre; guardados solo al registrar (es_parcial=false),
   * porque el API exige files en papel al finalizar. En guardado parcial no se re-suben guardados.
   */
  private async agregarArchivosRegistroAFormData(
    formData: FormData,
    esParcial: boolean
  ): Promise<void> {
    const partes = (
      await Promise.all(
        this.itemsRegistroArchivos.map(async (item) => {
          if (item.id_categoria == null) return null;
          if (item.file != null) {
            return { file: item.file, id_categoria: item.id_categoria };
          }
          if (!esParcial && item.adjuntoGuardado) {
            const file = await this.obtenerFileDesdeAdjuntoGuardado(item.adjuntoGuardado);
            return { file, id_categoria: item.id_categoria };
          }
          return null;
        })
      )
    ).filter((p): p is { file: File; id_categoria: number } => p != null);

    const vistos = new Set<string>();
    for (const parte of partes) {
      const clave = `${(parte.file.name ?? '').toLowerCase()}|${parte.id_categoria}`;
      if (vistos.has(clave)) continue;
      vistos.add(clave);
      formData.append('files', parte.file, parte.file.name);
      formData.append('id_categorias', parte.id_categoria.toString());
    }
  }

  /** Descarga un adjunto persistido y lo convierte en `File` para multipart. */
  private async obtenerFileDesdeAdjuntoGuardado(adj: AdjuntoItem): Promise<File> {
    const filePath = adj.ruta_almacenamiento?.trim();
    if (!filePath) {
      throw new Error('El adjunto guardado no tiene ruta de almacenamiento.');
    }
    const fallbackUrl = adj.url_descarga?.trim();
    let blob: Blob;
    try {
      blob = await firstValueFrom(this.negocioService.obtenerArchivoPorRuta(filePath));
    } catch (err) {
      const httpErr = err as HttpErrorResponse;
      if (httpErr?.status === 404 && fallbackUrl) {
        blob = await firstValueFrom(this.visitasService.descargarAdjunto(fallbackUrl));
      } else {
        throw err;
      }
    }
    const nombre = adj.nombre_archivo?.trim() || 'adjunto';
    const type = adj.mime_type?.trim() || blob.type || 'application/octet-stream';
    return new File([blob], nombre, { type });
  }

  /** Formatea una fecha ISO a yyyy-mm-dd para el API. */
  private formatearFechaVisitaYYYYMMDD(fechaISO: string): string {
    if (!fechaISO) return '';
    const d = new Date(fechaISO);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /**
   * Formatea la fecha y hora para enviar a la API
   */
  private formatearFechaHora(fechaISO: string, hora: string): string {
    if (!fechaISO || !hora) return '';
    
    const fecha = new Date(fechaISO);
    const [horas, minutos] = hora.split(':');
    fecha.setHours(parseInt(horas, 10), parseInt(minutos, 10), 0, 0);
    
    return fecha.toISOString();
  }

  /**
   * Obtiene el nombre del productor seleccionado
   */
  /**
   * Obtiene la descripción del tipo de visita actual
   */
  getTipoVisitaDescripcion(): string {
    const idTipo = this.formulario.get('id_tipo_visita')?.value;
    if (!idTipo) return '';
    const tipo = this.tiposVisita.find(t => t.id_tipo_visita === idTipo);
    return tipo ? tipo.descripcion : '';
  }

  getNombreProductor(idProductor: string): string {
    const productor = this.productores.find(p => p.id_productor === idProductor);
    return productor ? this.getNombreCompletoProductor(productor) : '';
  }

  /**
   * Formatea la fecha para mostrar en formato dd-mm-yyyy
   */
  formatearFechaMostrar(fechaISO: string): string {
    if (!fechaISO) return '';
    const fecha = new Date(fechaISO);
    const dia = String(fecha.getDate()).padStart(2, '0');
    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
    const año = fecha.getFullYear();
    return `${dia}-${mes}-${año}`;
  }

  /**
   * Formatea la hora para mostrar en formato hh:mm (AM/PM)
   */
  formatearHoraMostrar(hora: string): string {
    if (!hora) return '';
    const [horas, minutos] = hora.split(':');
    const horaNum = parseInt(horas, 10);
    const ampm = horaNum >= 12 ? 'PM' : 'AM';
    const hora12 = horaNum % 12 || 12;
    return `${String(hora12).padStart(2, '0')}:${minutos} ${ampm}`;
  }

  /** Fecha y hora programada a partir del detalle API (campo `fecha` ISO). */
  getFechaHoraProgramadaHistorial(visita: Visita): string {
    const f = visita?.fecha;
    if (f == null || String(f).trim() === '') return '';
    return `${this.formatearFechaHistorialVisita(f)}, ${this.formatearHoraHistorialVisita(f)}`;
  }

  /** Fecha de realización (`fecha_visita`). */
  getFechaRealizacionHistorial(visita: Visita): string {
    const fv = visita?.fecha_visita;
    if (fv == null || String(fv).trim() === '') return '';
    return this.formatearFechaMostrar(String(fv));
  }

  /** Sí / No / vacío (→ S/I con valorOSinInfo). */
  textoEsEmergenciaHistorial(visita: Visita): string {
    if (visita?.es_contingencia === true) return 'Sí';
    if (visita?.es_contingencia === false) return 'No';
    return '';
  }

  textoBolsaVisitaHistorial(visita: Visita): string {
    if (visita?.bolsa_visita === true) return 'Sí';
    if (visita?.bolsa_visita === false) return 'No';
    return '';
  }

  mostrarDescripcionEmergenciaHistorial(visita: Visita): boolean {
    return visita?.es_contingencia === true;
  }

  getDescripcionVisitaHistorial(visita: Visita): string {
    return obtenerDescripcionVisita(visita);
  }

  esVisitaPlanificadaHistorial(visita: Visita): boolean {
    return (visita?.estado ?? '').trim().toLowerCase() === 'planificada';
  }

  getFechaInicioPlanificadaHistorial(visita: Visita): string {
    const f = visita?.fecha_inicio_planificada ?? visita?.registro_visita?.fecha_inicio_planificada;
    if (!f?.trim()) return '';
    return `${this.formatearFechaHistorialVisita(f)}, ${this.formatearHoraHistorialVisita(f)}`;
  }

  getFechaTerminoPlanificadaHistorial(visita: Visita): string {
    const f = visita?.fecha_termino_planificada ?? visita?.registro_visita?.fecha_termino_planificada;
    if (!f?.trim()) return '';
    return `${this.formatearFechaHistorialVisita(f)}, ${this.formatearHoraHistorialVisita(f)}`;
  }

  getClaseEmergenciaHistorial(visita: Visita): string {
    if (visita?.es_contingencia === true) return 'emergencia-si';
    if (visita?.es_contingencia === false) return 'emergencia-no';
    return 'emergencia-default';
  }

  /**
   * Presentación de `tipo_reporte` (API en minúsculas: digital / papel) para Ver Detalles.
   */
  formatearTipoReporte(val: string | null | undefined): string {
    const s = String(val ?? '').trim().toLowerCase();
    if (s === 'digital') return 'Digital';
    if (s === 'papel') return 'Papel';
    if (!s) return '';
    return String(val).trim();
  }

  /** Categoría legible para el bloque de adjuntos del historial (nombre API o id numérico). */
  textoCategoriaAdjuntoHistorial(adj: AdjuntoItem): string {
    const n = adj?.categoria_nombre?.trim();
    if (n) return n;
    if (adj?.id_categoria != null && adj.id_categoria !== undefined) {
      return `Categoría ${adj.id_categoria}`;
    }
    return '';
  }

  adjuntoResumenFirmado(detalle: Visita): VisitaResumenFirmadoAdjunto | null {
    return extraerAdjuntoResumenFirmadoDesdeVisita(detalle);
  }

  abrirResumenFirmado(detalle: Visita): void {
    const adj = this.adjuntoResumenFirmado(detalle);
    if (!adj) return;
    abrirAdjuntoUtil(adj, this.negocioService, this.visitasService, this.toastService);
  }

  textoFechaSubidaResumen(adj: VisitaResumenFirmadoAdjunto): string {
    const raw = adj.fecha_subida?.trim();
    if (!raw) return '';
    try {
      return new Date(raw).toLocaleString('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return raw;
    }
  }

  abrirODescargarAdjunto(adjunto: AdjuntoItem): void {
    abrirAdjuntoUtil(adjunto, this.negocioService, this.visitasService, this.toastService);
  }

mostrarDatetime = true;

forzarRecreacionDatetime() {
  const fecha = this.fechaProgramadaISO;
  this.fechaProgramadaISO = null;
  setTimeout(() => this.fechaProgramadaISO = fecha);
}


}
